import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { StatusBadge } from './AuditShared';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { findingsService, Finding } from '../../services/auditService';
import { extractApiError } from '../../utils/apiError';
import FindingDetailModal from './FindingDetailModal';
import NoDataFound from '../common/NoDataFound';
import Skeleton from '../common/Skeleton';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

type SeverityFilter = 'all' | 'material' | 'trivial';
type StatusFilter   = 'open' | 'closed' | 'all';

const isoDate = (d: Date) => d.toISOString().slice(0, 10);
const today = () => isoDate(new Date());
const ninetyDaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return isoDate(d);
};
const parseLocalDate = (s: string): Date | undefined => {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};

const AuditFindings: React.FC = () => {
  const { formatDate } = useEntitySettings();
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [status,   setStatus]   = useState<StatusFilter>('open');
  const [from,     setFrom]     = useState<string>(ninetyDaysAgo());
  const [to,       setTo]       = useState<string>(today());

  const [rows,  setRows]  = useState<Finding[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [selectedFinding, setSelectedFinding] = useState<Finding | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const filterParams = useMemo(() => ({
    is_trivial: severity === 'all' ? undefined : severity === 'trivial',
    is_closed:  status   === 'all' ? undefined : status   === 'closed',
    raised_on_date_from: from || undefined,
    raised_on_date_to:   to   || undefined,
  }), [severity, status, from, to]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await findingsService.list({
        ...filterParams,
        page,
        limit: pageSize,
      });
      setRows(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setToast({ type: 'error', message: extractApiError(e, 'Failed to load findings.') });
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [severity, status, from, to, pageSize]);

  const openModal = (f: Finding) => { setSelectedFinding(f); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setSelectedFinding(null); };

  return (
    <div className="netbody-card flex flex-col h-full">

      {/* Toolbar — severity + status toggles + date range RIGHT */}
      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            data-testid="SEL_finding_severity"
            value={severity}
            onChange={v => setSeverity((v ?? 'all') as SeverityFilter)}
            className="filter-status"
            noSearchIcon
            options={[
              { value: 'all', label: 'All Severity' },
              { value: 'material', label: 'Material' },
              { value: 'trivial', label: 'Trivial' },
            ]}
          />

          <FormControl.SearchSelect
            data-testid="SEL_finding_status"
            value={status}
            onChange={v => setStatus((v ?? 'open') as StatusFilter)}
            className="filter-status"
            noSearchIcon
            options={[
              { value: 'open', label: 'Open' },
              { value: 'closed', label: 'Closed' },
              { value: 'all', label: 'All Status' },
            ]}
          />

          <FormControl.DatePicker
            data-testid="INP_finding_date_from"
            value={from}
            onChange={e => setFrom(e.target.value)}
            className="filter-date"
            placeholder="From date"
            maxDate={parseLocalDate(to)}
          />
          <FormControl.DatePicker
            data-testid="INP_finding_date_to"
            value={to}
            onChange={e => setTo(e.target.value)}
            className="filter-date"
            placeholder="To date"
            minDate={parseLocalDate(from)}
          />
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Findings text</th>
                    <th className="table-list-th">Procedure</th>
                    <th className="table-list-th">Raised on</th>
                    <th className="table-list-th table-list-cell--priority">Severity</th>
                    <th className="table-list-th">Response user</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['3xs', 'md', '2xs', 'xs', 'pill', 'xs', 'pill']} />}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="table-list-cell">
                        <NoDataFound
                          title="No Findings Found"
                          message="No findings match the selected filters."
                        />
                      </td>
                    </tr>
                  )}
                  {!loading && rows.map((f, idx) => (
                    <tr
                      key={f.id}
                      data-testid={`BTN_finding_row_${f.id}_open`}
                      className="table-list-row"
                      onClick={() => openModal(f)}
                    >
                      <td className="table-list-cell table-list-cell--sno">
                        {(page - 1) * pageSize + idx + 1}
                      </td>
                      <td
                        className="table-list-cell table-list-cell--truncate"
                        title={f.findings_text}
                      >
                        {f.findings_text}
                      </td>
                      <td className="table-list-cell">{(f as any).procedure_name ?? '—'}</td>
                      <td className="table-list-cell">{formatDate(f.raised_on_date)}</td>
                      <td className="table-list-cell">
                        <StatusBadge status={f.is_trivial ? 'Trivial' : 'Material'} />
                      </td>
                      <td className="table-list-cell">
                        {(f as any).response_user_name ?? (f.response_user_id ? 'Unknown user' : '—')}
                      </td>
                      <td className="table-list-cell">
                        <StatusBadge status={f.is_closed ? 'Closed' : 'Open'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="findings"
            />
          </div>
        </div>
      </div>

      <FindingDetailModal
        isOpen={modalOpen}
        onClose={closeModal}
        finding={selectedFinding}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default AuditFindings;
